/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext } from '@gh/builder-shared';
import { getRouteInitData, queryFormEditable, State } from '@gh/common-shared';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { combineLatest$, of$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';
import { isEmpty, filter } from 'lodash';
import { Observable } from 'rxjs';

import {
  getQuoteVariationId,
  isQuoteVariationTotal,
  quoteTotalFromDerivedInfo,
  QuoteVariation,
  QuoteVariationTotal,
} from '../../../resources/quote.model';
import { QuoteDetailsCommandService } from '../quote-details-command.service';
import {
  queryQuoteDerivedInfo,
  queryQuoteIsProfitSupported,
  queryQuoteIsProfitVisible,
  queryQuoteVariationFocusedCell,
  queryQuoteVariations, queryQuoteVariationsFilter,
  QuoteDetailsState,
  VariationsFilter,
} from '../quote-details.state';
import { QuoteDetailsVariationGridDefinitionService } from './quote-details-variation-grid-definition.service';
import { queryCommunicationListFilters } from '../../lead-details/lead-details.state';

@Component({
  selector: 'gh-quote-details-variation-list',
  template: require('./quote-details-variation-list.component.html'),
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QuoteDetailsVariationGridDefinitionService],
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
})
export class QuoteDetailsVariationListComponent implements OnInit {
  variations$: Observable<QuoteVariation[]>;
  filters$: Observable<VariationsFilter>;
  commonVariations: QuoteVariation[];
  total$: Observable<QuoteVariationTotal[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  supportsProfit$: Observable<boolean>;
  isProfitVisible$: Observable<boolean>;
  editable$: Observable<boolean>;

  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext,
              private changeDetectorRef: ChangeDetectorRef,
              private state: State<QuoteDetailsState>,
              private commands: QuoteDetailsCommandService,
              public variationGridDefinition: QuoteDetailsVariationGridDefinitionService) {

  }

  ngOnInit(): void {
    const { variationGridLayout } = getRouteInitData(this.route);

    this.variationGridDefinition.bind(variationGridLayout);

    this.supportsProfit$ = this.state.select(queryQuoteIsProfitSupported);
    this.isProfitVisible$ = this.state.select(queryQuoteIsProfitVisible);

    this.filters$ = this.state.select(queryQuoteVariationsFilter);
    // @ts-ignore
    this.variations$ = combineLatest$(this.filters$, this.state.select(queryQuoteVariations)).pipe(
      map$(([ {search}, variations ]) => {
        if (!search) {
          return variations;
        } else {
          return filter(variations, (variation) =>
            variation.description.toLowerCase().includes(search.toLowerCase()) ||
            variation.itemCode.toLowerCase().includes(search.toLowerCase()));
        }
      }));
    this.total$ = combineLatest$(this.isProfitVisible$, this.state.select(queryQuoteDerivedInfo)).pipe(
      map$(([isProfitVisible, info]) => [quoteTotalFromDerivedInfo(info, isProfitVisible)]));
    this.focusedCell$ = this.state.select(queryQuoteVariationFocusedCell);

    this.editable$ = this.state.select(queryFormEditable);
  }

  onClone(): void {
    this.commands.cloneVariations();
  }

  onAdd(): void {
    this.commands.addVariations();
  }

  onMoveCreditDebit(variation: QuoteVariation): void {
    this.commands.moveVariationCreditDebit(getQuoteVariationId(variation));
  }

  onViewMemo(variation: QuoteVariation): void {
    this.commands.viewVariationMemo(getQuoteVariationId(variation));
  }

  onRemove(variation: QuoteVariation): void {
    this.commands.removeVariation(getQuoteVariationId(variation));
  }

  onCloneQuote(variation: QuoteVariation): void {
    this.commands.cloneQuoteVariation(getQuoteVariationId(variation));
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  canMoveToDebit(variation: QuoteVariation): boolean {
    return variation.isCredit && variation.itemPrice > 0;
  }

  canMoveToCredit({ isCredit, origQuantity, quantity, itemPrice }: QuoteVariation): boolean {
    return !isCredit && origQuantity > 0 && quantity <= origQuantity && itemPrice > 0;
  }

  hasMemo(variation: QuoteVariation): boolean {
    return !isEmpty(variation.memo);
  }

  isVariation(variation: QuoteVariation): boolean {
    return !isQuoteVariationTotal(variation);
  }

  onSearch(event: string): void {
    this.commands.setVariationListFilter({
       search: event,
     });
  }
}
